import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Upload, RefreshCw } from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { AnalisiCard } from '@/components/Cards';
import { useAnalisi } from '@/hooks/useAnalisi';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

export default function Analisi() {
  const navigate = useNavigate();
  const { analisi, loading, error, fetchAnalisi, deleteAnalisi } = useAnalisi();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAnalisiId, setSelectedAnalisiId] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchAnalisi();
  }, [fetchAnalisi]);

  useEffect(() => {
    load();
  }, [load]);

  const hasElaborazione = analisi.some(a => a.stato === 'elaborazione' || a.stato === 'bozza');

  useEffect(() => {
    if (!hasElaborazione) return;

    const interval = setInterval(() => {
      load();
    }, 8000);

    return () => clearInterval(interval);
  }, [hasElaborazione, load]);

  const handleDeleteAnalisi = async () => {
    if (!selectedAnalisiId) return;
    try {
      await deleteAnalisi(selectedAnalisiId);
      toast.success('Analisi eliminata con successo');
      setDeleteDialogOpen(false);
      setSelectedAnalisiId(null);
    } catch {
      toast.error("Errore nell'eliminazione dell'analisi");
    }
  };

  return (
    <DashboardLayout title="Analisi">
      <div className="space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Analisi</h1>
            <p className="text-muted-foreground mt-2">
              {analisi.length > 0
                ? `${analisi.length} analisi trovate`
                : 'Gestisci e visualizza tutte le analisi dei consuntivi'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            <Button
              onClick={() => navigate(ROUTE_PATHS.DOCUMENTI)}
              size="lg"
              className="gap-2 shrink-0"
            >
              <Upload className="h-5 w-5" />
              Nuova Analisi
            </Button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            <strong>Errore:</strong> {error}
            <Button variant="link" size="sm" className="ml-2 text-destructive" onClick={load}>
              Riprova
            </Button>
          </div>
        )}

        {hasElaborazione && (
          <div className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-primary flex items-center gap-3">
            <RefreshCw className="h-4 w-4 animate-spin shrink-0" />
            <span>Analisi in elaborazione... La pagina si aggiornerà automaticamente ogni 8 secondi.</span>
          </div>
        )}

        {loading && analisi.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} className="h-[280px] w-full rounded-lg" />
            ))}
          </div>
        ) : analisi.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-16 px-4 text-center"
          >
            <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mb-6">
              <FileText className="h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Nessuna analisi trovata</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Carica un documento dalla sezione Documenti per generare un'analisi.
            </p>
            <Button onClick={() => navigate(ROUTE_PATHS.DOCUMENTI)} className="gap-2">
              <Upload className="h-4 w-4" />
              Vai a Documenti
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence mode="popLayout">
              {analisi.map((a, index) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <AnalisiCard
                    analisi={a}
                    onView={() => navigate(`/analisi/${a.id}`)}
                    onDelete={() => {
                      setSelectedAnalisiId(a.id);
                      setDeleteDialogOpen(true);
                    }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa analisi? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAnalisi} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
